import { BowtieDiagram, BowtieNode, BowtieEdge } from './bowtie.types';
import { Node, Edge, MarkerType } from '@xyflow/react';

export function computeBowtieLayout(diagram: BowtieDiagram): {
  nodes: Node[];
  edges: Edge[];
} {
  const threats = (diagram.nodes || []).filter((n) => n.type === 'threat');
  const prevention = (diagram.nodes || []).filter((n) => n.type === 'preventionBarrier');
  const hazard = (diagram.nodes || []).find((n) => n.type === 'hazard');
  const topEvent = (diagram.nodes || []).find((n) => n.type === 'topEvent');
  const mitigation = (diagram.nodes || []).filter((n) => n.type === 'mitigationBarrier');
  const consequences = (diagram.nodes || []).filter((n) => n.type === 'consequence');

  // Unified Dimensions
  const wThreat = 240;
  const wBarrier = 260;
  const wTopEvent = 300;
  const wHazard = 280;
  const wConsequence = 250;
  const cardHeight = 108; // unified height for Threats, Barriers, Consequences

  const barrierGapX = 70; // horizontal gap between Threat and Barrier, or between barriers
  const knotGapX = 130; // convergence gap before Top Event left handle, and divergence gap after Top Event right handle

  // Group prevention barriers per threat
  const prevByThreat = new Map<string, BowtieNode[]>();
  const handledPrevIds = new Set<string>();

  threats.forEach((t) => {
    const tCauseId = t.metadata?.causeId || t.id.replace('threat-', '');
    const assigned = prevention.filter(
      (p) =>
        p.metadata?.threatId === tCauseId ||
        p.metadata?.threatId === t.id ||
        p.metadata?.causeId === tCauseId
    );
    assigned.forEach((p) => handledPrevIds.add(p.id));
    prevByThreat.set(t.id, assigned);
  });

  // Any unassigned prevention barriers
  const unassignedPrev = prevention.filter((p) => !handledPrevIds.has(p.id));
  if (unassignedPrev.length > 0 && threats.length > 0) {
    const existing = prevByThreat.get(threats[0].id) || [];
    prevByThreat.set(threats[0].id, [...existing, ...unassignedPrev]);
  }

  // Group mitigation barriers per consequence
  const mitByConsequence = new Map<string, BowtieNode[]>();
  const handledMitIds = new Set<string>();

  consequences.forEach((c) => {
    const cConsId = c.metadata?.consequenceId || c.id.replace('consequence-', '');
    const assigned = mitigation.filter(
      (m) =>
        m.metadata?.consequenceId === cConsId ||
        m.metadata?.consequenceId === c.id
    );
    assigned.forEach((m) => handledMitIds.add(m.id));
    mitByConsequence.set(c.id, assigned);
  });

  // Any unassigned mitigation barriers
  const unassignedMit = mitigation.filter((m) => !handledMitIds.has(m.id));
  if (unassignedMit.length > 0 && consequences.length > 0) {
    const existing = mitByConsequence.get(consequences[0].id) || [];
    mitByConsequence.set(consequences[0].id, [...existing, ...unassignedMit]);
  }

  // Calculate maximum barrier columns on each wing for symmetrical geometry
  let maxLeftCols = 0;
  threats.forEach((t) => {
    maxLeftCols = Math.max(maxLeftCols, (prevByThreat.get(t.id) || []).length);
  });
  let maxRightCols = 0;
  consequences.forEach((c) => {
    maxRightCols = Math.max(maxRightCols, (mitByConsequence.get(c.id) || []).length);
  });

  const wingspanCols = Math.max(maxLeftCols, maxRightCols, 1);
  const wingWidth = wingspanCols * (wBarrier + barrierGapX) + knotGapX;

  // Geometry: Top Event and Hazard at the EXACT CENTER
  const colThreatX = 60;
  const threatRightEdge = colThreatX + wThreat; // 300
  const topEventX = threatRightEdge + wingWidth;
  const topEventRightEdge = topEventX + wTopEvent;
  const colConsequenceX = topEventRightEdge + wingWidth;

  // Vertical Row Pitch and Center Y
  const rowPitchY = 170;
  const numThreatRows = Math.max(threats.length, 1);
  const numConsequenceRows = Math.max(consequences.length, 1);
  const maxRows = Math.max(numThreatRows, numConsequenceRows, 2);

  const totalHeight = (maxRows - 1) * rowPitchY;
  const centerY = Math.max(340, totalHeight / 2 + 160);

  const flowNodes: Node[] = [];
  const flowEdges: Edge[] = [];

  // 1. Center Knot: Top Event
  if (topEvent) {
    flowNodes.push({
      id: topEvent.id,
      type: 'topEventNode',
      position: { x: topEventX, y: centerY - 80 },
      data: { ...topEvent },
    });
  }

  // 2. Center: Hazard Context directly above Top Event
  if (hazard && topEvent) {
    const hazardX = topEventX + (wTopEvent - wHazard) / 2;
    flowNodes.push({
      id: hazard.id,
      type: 'hazardNode',
      position: { x: hazardX, y: centerY - 80 - 150 },
      data: { ...hazard },
    });

    flowEdges.push({
      id: `edge-${hazard.id}-${topEvent.id}`,
      source: hazard.id,
      target: topEvent.id,
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'smoothstep',
      style: { stroke: '#f59e0b', strokeWidth: 2.2 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#f59e0b',
        width: 14,
        height: 14,
      },
    });
  }

  // 3. Left Wing: Threats & Horizontal Prevention Barriers
  threats.forEach((t, rowIdx) => {
    const rowY = centerY + (rowIdx - (numThreatRows - 1) / 2) * rowPitchY;
    const nodeY = rowY - cardHeight / 2;

    // Threat Node at leftmost column
    flowNodes.push({
      id: t.id,
      type: 'threatNode',
      position: { x: colThreatX, y: nodeY },
      data: { ...t },
    });

    const assignedPreventions = prevByThreat.get(t.id) || [];

    if (assignedPreventions.length === 0) {
      // Direct edge Threat -> Top Event left handle
      if (topEvent) {
        flowEdges.push({
          id: `edge-${t.id}-${topEvent.id}`,
          source: t.id,
          target: topEvent.id,
          sourceHandle: 'right',
          targetHandle: 'left',
          type: 'smoothstep',
          style: { stroke: '#ea580c', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#ea580c',
            width: 14,
            height: 14,
          },
        });
      }
    } else {
      // Layout prevention barriers horizontally to the right of the Threat
      let prevSourceId = t.id;

      assignedPreventions.forEach((p, pIdx) => {
        const barrierX = threatRightEdge + barrierGapX + pIdx * (wBarrier + barrierGapX);

        flowNodes.push({
          id: p.id,
          type: 'barrierNode',
          position: { x: barrierX, y: nodeY },
          data: { ...p },
        });

        const isFailed = p.metadata?.status === 'failed' || (p.metadata?.effectivenessScore ?? 70) < 40;

        // Edge from previous node to this barrier
        flowEdges.push({
          id: `edge-${prevSourceId}-${p.id}`,
          source: prevSourceId,
          target: p.id,
          sourceHandle: 'right',
          targetHandle: 'left',
          type: 'smoothstep',
          animated: isFailed,
          style: {
            stroke: isFailed ? '#ef4444' : '#64748b',
            strokeWidth: isFailed ? 2.5 : 2,
            strokeDasharray: isFailed ? '4,4' : undefined,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isFailed ? '#ef4444' : '#64748b',
            width: 14,
            height: 14,
          },
        });

        prevSourceId = p.id;
      });

      // The LAST prevention barrier connects into Top Event left handle
      if (topEvent) {
        const lastPrev = assignedPreventions[assignedPreventions.length - 1];
        const isFailed = lastPrev.metadata?.status === 'failed' || (lastPrev.metadata?.effectivenessScore ?? 70) < 40;

        flowEdges.push({
          id: `edge-${lastPrev.id}-${topEvent.id}`,
          source: lastPrev.id,
          target: topEvent.id,
          sourceHandle: 'right',
          targetHandle: 'left',
          type: 'smoothstep',
          animated: isFailed,
          style: {
            stroke: isFailed ? '#ef4444' : '#64748b',
            strokeWidth: isFailed ? 2.5 : 2,
            strokeDasharray: isFailed ? '4,4' : undefined,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isFailed ? '#ef4444' : '#64748b',
            width: 14,
            height: 14,
          },
        });
      }
    }
  });

  // 4. Right Wing: Consequences & Horizontal Mitigation Barriers
  consequences.forEach((c, rowIdx) => {
    const rowY = centerY + (rowIdx - (numConsequenceRows - 1) / 2) * rowPitchY;
    const nodeY = rowY - cardHeight / 2;

    // Consequence Node at rightmost column
    flowNodes.push({
      id: c.id,
      type: 'consequenceNode',
      position: { x: colConsequenceX, y: nodeY },
      data: { ...c },
    });

    const assignedMitigations = mitByConsequence.get(c.id) || [];
    const numMit = assignedMitigations.length;

    if (numMit === 0) {
      // Direct edge Top Event right handle -> Consequence left handle
      if (topEvent) {
        flowEdges.push({
          id: `edge-${topEvent.id}-${c.id}`,
          source: topEvent.id,
          target: c.id,
          sourceHandle: 'right',
          targetHandle: 'left',
          type: 'smoothstep',
          style: { stroke: '#ef4444', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#ef4444',
            width: 14,
            height: 14,
          },
        });
      }
    } else {
      // Position mitigation barriers between Top Event and Consequence.
      // They are placed horizontally adjacent to the consequence, extending leftward:
      // M[0] (closest to Top Event) -> ... -> M[numMit-1] (closest to Consequence) -> Consequence
      assignedMitigations.forEach((m, mIdx) => {
        const barrierX = colConsequenceX - (numMit - mIdx) * (wBarrier + barrierGapX);

        flowNodes.push({
          id: m.id,
          type: 'barrierNode',
          position: { x: barrierX, y: nodeY },
          data: { ...m },
        });
      });

      // Edge 1: Top Event right handle -> M[0] left handle
      if (topEvent) {
        const firstMit = assignedMitigations[0];
        const isFailed = firstMit.metadata?.status === 'failed' || (firstMit.metadata?.effectivenessScore ?? 70) < 40;

        flowEdges.push({
          id: `edge-${topEvent.id}-${firstMit.id}`,
          source: topEvent.id,
          target: firstMit.id,
          sourceHandle: 'right',
          targetHandle: 'left',
          type: 'smoothstep',
          animated: isFailed,
          style: {
            stroke: isFailed ? '#ef4444' : '#64748b',
            strokeWidth: isFailed ? 2.5 : 2,
            strokeDasharray: isFailed ? '4,4' : undefined,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isFailed ? '#ef4444' : '#64748b',
            width: 14,
            height: 14,
          },
        });
      }

      // Intermediate edges: M[i] -> M[i+1]
      for (let i = 0; i < numMit - 1; i++) {
        const currMit = assignedMitigations[i];
        const nextMit = assignedMitigations[i + 1];
        const isFailed = currMit.metadata?.status === 'failed' || (currMit.metadata?.effectivenessScore ?? 70) < 40;

        flowEdges.push({
          id: `edge-${currMit.id}-${nextMit.id}`,
          source: currMit.id,
          target: nextMit.id,
          sourceHandle: 'right',
          targetHandle: 'left',
          type: 'smoothstep',
          animated: isFailed,
          style: {
            stroke: isFailed ? '#ef4444' : '#64748b',
            strokeWidth: isFailed ? 2.5 : 2,
            strokeDasharray: isFailed ? '4,4' : undefined,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isFailed ? '#ef4444' : '#64748b',
            width: 14,
            height: 14,
          },
        });
      }

      // Final edge: Last Mitigation -> Consequence
      const lastMit = assignedMitigations[numMit - 1];
      const isFailed = lastMit.metadata?.status === 'failed' || (lastMit.metadata?.effectivenessScore ?? 70) < 40;

      flowEdges.push({
        id: `edge-${lastMit.id}-${c.id}`,
        source: lastMit.id,
        target: c.id,
        sourceHandle: 'right',
        targetHandle: 'left',
        type: 'smoothstep',
        animated: isFailed,
        style: {
          stroke: isFailed ? '#ef4444' : '#64748b',
          strokeWidth: isFailed ? 2.5 : 2,
          strokeDasharray: isFailed ? '4,4' : undefined,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isFailed ? '#ef4444' : '#64748b',
          width: 14,
          height: 14,
        },
      });
    }
  });

  return { nodes: flowNodes, edges: flowEdges };
}
